import { useEffect, useState, useCallback } from "react"
import { api, urls } from '../../../services/api'
import { IBanner } from '../../../types/banner'

import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Section } from "../../ui/Section";
import { Skeleton } from "../../ui/Skeleton";
import { SectionTitle } from "../../ui/SectionTitle";
import { Star } from 'lucide-react';

import { useApp } from '../../../context/App';

interface Props {
	mobile: boolean
}

export function Banner({ mobile }: Props) {
	const { getTenantUrl } = useApp();
	const [banners, setBanners] = useState<IBanner[]>([])
	const [loading, setLoading] = useState(true);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isAutoPlaying, setIsAutoPlaying] = useState(true);

	const fetchBanners = useCallback(async () => {
		try {
			const response = await api.get<IBanner[]>(urls.Banners)
			const filtered = response.data.filter(item =>
				mobile ? item.banner_periferico?.toLowerCase() === 'mb' : item.banner_periferico?.toLowerCase() === 'pc'
			);
			setBanners(filtered);
		} catch (error) {
			// console.error('Banner fetch error:', error);
		} finally {
			setLoading(false);
		}
	}, [mobile]);

	useEffect(() => {
		fetchBanners();
	}, [fetchBanners]);

	useEffect(() => {
		if (!isAutoPlaying || banners.length <= 1) return;

		const timer = setInterval(() => {
			setCurrentIndex((prev) => (prev + 1) % banners.length);
		}, 5000);

		return () => clearInterval(timer);
	}, [isAutoPlaying, banners.length]);

	const nextSlide = () => {
		setCurrentIndex((prev) => (prev + 1) % banners.length);
	};

	const prevSlide = () => {
		setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
	};

	if (loading) {
		return (
			<Section className="bg-white dark:bg-surface-dark">
				<div className="w-full flex items-center justify-center gap-6 lg:gap-8 mb-8">
					<div className="flex-1 h-px bg-border-default/20" />
					<Skeleton className="h-8 w-48" />
					<div className="flex-1 h-px bg-border-default/20" />
				</div>
				<Skeleton className="w-full aspect-[1110/324] lg:aspect-[1110/162] rounded-3xl" />
			</Section>
		);
	}

	if (banners.length === 0) return null;

	return (
		<Section
			className="bg-white dark:bg-surface-dark transition-colors"
			onMouseEnter={() => setIsAutoPlaying(false)}
			onMouseLeave={() => setIsAutoPlaying(true)}
		>
			<SectionTitle
				title="Avisos"
				icon={<Star className="text-secondary" size={24} />}
			/>
			<div className="relative group w-full aspect-[1110/324] lg:aspect-[1110/162]">
				<AnimatePresence mode="wait">
					<motion.div
						key={currentIndex}
						initial={{ opacity: 0, scale: 1.05 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.95 }}
						transition={{ duration: 0.6, ease: "easeInOut" }}
						className="w-full h-full"
					>
						{(() => {
							const rawLink = banners[currentIndex].link || '/';
							const processedLink = rawLink.replace('https://itajai.sc.gov.br', '').replace('http://itajai.sc.gov.br', '');
							const isInternal = rawLink.includes('itajai.sc.gov.br') || !rawLink.startsWith('http');

							return (
								<a
									key={currentIndex}
									href={getTenantUrl(processedLink)}
									target={isInternal ? '_self' : '_blank'}
									rel={isInternal ? "" : "noreferrer"}
									className="block w-full h-full"
								>
									<img
										src={banners[currentIndex].nome_arquivo}
										className="w-full h-full object-cover rounded-3xl shadow-premium dark:shadow-premium-dark select-none border border-white/10"
										alt={banners[currentIndex].titulo || `Banner ${currentIndex}`}
										loading={currentIndex === 0 ? "eager" : "lazy"}
										{...({ fetchpriority: currentIndex === 0 ? "high" : "auto" } as any)}
									/>
								</a>
							);
						})()}
					</motion.div>
				</AnimatePresence>

				{/* Controls */}
				{banners.length > 1 && (
					<>
						<button
							onClick={prevSlide}
							className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-md p-3 rounded-full text-white transition-all opacity-0 group-hover:opacity-100 border-none cursor-pointer"
						>
							<ChevronLeft size={24} />
						</button>
						<button
							onClick={nextSlide}
							className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-md p-3 rounded-full text-white transition-all opacity-0 group-hover:opacity-100 border-none cursor-pointer"
						>
							<ChevronRight size={24} />
						</button>
					</>
				)}
			</div>

			{/* Indicators Below Banner */}
			{banners.length > 1 && (
				<div className="flex justify-center gap-2 mt-6">
					{banners.map((_, idx) => (
						<button
							key={idx}
							onClick={() => setCurrentIndex(idx)}
							className={`h-2 rounded-full transition-all duration-300 border-none cursor-pointer ${idx === currentIndex
								? 'w-10 bg-secondary shadow-lg shadow-secondary/20'
								: 'w-2.5 bg-primary/20 dark:bg-white/20 hover:bg-primary/40 dark:hover:bg-white/40'
								}`}
							aria-label={`Ir para o banner ${idx + 1}`}
						/>
					))}
				</div>
			)}
		</Section>
	);
}